import React, { useEffect, useMemo, useRef, useState } from 'react';
import http from '../services/api';
import { Play, Pause, RotateCcw, Volume2, Loader2, HeartHandshake } from 'lucide-react';

const BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * Split the PSA body into cinematic "beats" (sentences / short lines) and
 * assign each beat a fractional [start, end] window inside the audio duration.
 * Weighting by character length keeps short lines from lingering too long
 * and long lines from flying by.
 */
function buildBeats(body) {
  if (!body || !body.trim()) return [];
  // Break on sentence terminators OR blank lines. Keep the terminator glued
  // to the sentence it ended so the rendered text still reads naturally.
  const raw = body
    .replace(/\r\n/g, '\n')
    .split(/(?<=[.!?…])\s+|\n{2,}/g)
    .map(s => s.trim())
    .filter(Boolean);

  const total = raw.reduce((acc, s) => acc + Math.max(s.length, 6), 0) || 1;
  let cursor = 0;
  return raw.map((text, i) => {
    const weight = Math.max(text.length, 6) / total;
    const start = cursor;
    const end = i === raw.length - 1 ? 1 : cursor + weight;
    cursor = end;
    return { index: i, text, start, end };
  });
}

/**
 * Shared body for both the homepage modal and the /psa route.
 * - Fetches /api/psa (heading, body, audio URL, isPlaceholder flag).
 * - Autoplays narration on mount; falls back to a "Tap to play" state
 *   whenever the browser blocks autoplay (Safari/iOS/most mobile).
 * - Karaoke-style sync: sentences highlight + auto-scroll with the audio.
 */
export default function PsaContent({ autoPlay = true }) {
  const [psa, setPsa] = useState(null);
  const [err, setErr] = useState('');
  const [playing, setPlaying] = useState(false);
  const [ready, setReady] = useState(false);
  const [needsTap, setNeedsTap] = useState(false);
  const [progress, setProgress] = useState(0);   // 0..1
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);

  useEffect(() => {
    let alive = true;
    http.get('/psa')
      .then(r => { if (alive) setPsa(r.data); })
      .catch(e => { if (alive) setErr(e?.response?.data?.detail || 'Could not load the PSA. Please refresh.'); });
    return () => { alive = false; };
  }, []);

  const audioUrl = useMemo(() => (psa?.audioUrl ? `${BASE}${psa.audioUrl}` : ''), [psa?.audioUrl]);
  const beats = useMemo(() => buildBeats(psa?.body || ''), [psa?.body]);

  // Attempt autoplay whenever the audio element is ready + we have a URL.
  useEffect(() => {
    if (!autoPlay || !audioUrl || !audioRef.current) return;
    const el = audioRef.current;
    const tryPlay = () => {
      el.play().then(() => {
        setPlaying(true);
        setNeedsTap(false);
      }).catch(() => {
        setPlaying(false);
        setNeedsTap(true);
      });
    };
    if (el.readyState >= 2) tryPlay();
    else el.addEventListener('canplay', tryPlay, { once: true });
    return () => el.removeEventListener('canplay', tryPlay);
  }, [audioUrl, autoPlay]);

  const play = () => {
    const el = audioRef.current;
    if (!el) return;
    el.play().then(() => { setPlaying(true); setNeedsTap(false); }).catch(() => setNeedsTap(true));
  };
  const pause = () => {
    const el = audioRef.current;
    if (!el) return;
    el.pause();
    setPlaying(false);
  };
  const replay = () => {
    const el = audioRef.current;
    if (!el) return;
    el.currentTime = 0;
    play();
  };

  const fmt = (s) => {
    if (!isFinite(s) || s < 0) return '0:00';
    const m = Math.floor(s / 60);
    const r = Math.floor(s % 60);
    return `${m}:${String(r).padStart(2, '0')}`;
  };

  // Which beat is "active" right now? The last beat whose start <= progress.
  const activeIndex = useMemo(() => {
    if (!beats.length) return -1;
    // If we're at the very start (0) and haven't played yet, nothing is active.
    if (progress <= 0) return 0;
    for (let i = beats.length - 1; i >= 0; i--) {
      if (progress >= beats[i].start) return i;
    }
    return 0;
  }, [beats, progress]);

  // Auto-scroll the beats container to follow the active beat.
  const scrollWrapRef = useRef(null);
  const beatRefs = useRef([]);
  const lastScrolledRef = useRef(-1);
  useEffect(() => {
    if (activeIndex < 0) return;
    if (lastScrolledRef.current === activeIndex) return;
    lastScrolledRef.current = activeIndex;
    const wrap = scrollWrapRef.current;
    const target = beatRefs.current[activeIndex];
    if (!wrap || !target) return;
    // Center the active beat inside the scroll wrapper (smooth). Using
    // native scrollTo instead of scrollIntoView so we never move the
    // outer page/modal accidentally.
    const wrapRect = wrap.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const offset = (targetRect.top - wrapRect.top) + wrap.scrollTop
                 - (wrap.clientHeight / 2) + (target.clientHeight / 2);
    wrap.scrollTo({ top: Math.max(0, offset), behavior: 'smooth' });
  }, [activeIndex]);

  if (err) {
    return (
      <div className="p-6 text-center" data-testid="psa-error">
        <div className="text-sm text-red-700">{err}</div>
      </div>
    );
  }
  if (!psa) {
    return (
      <div className="p-10 flex items-center justify-center text-neutral-500" data-testid="psa-loading">
        <Loader2 className="animate-spin mr-2" size={18}/> Loading…
      </div>
    );
  }

  return (
    <div className="text-neutral-900" data-testid="psa-content">
      {/* Header */}
      <div className="px-6 md:px-10 pt-8 pb-6 bg-gradient-to-br from-green-900 via-emerald-800 to-green-950 text-white relative overflow-hidden">
        <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-amber-300/10 blur-3xl"/>
        <div className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full bg-emerald-300/10 blur-3xl"/>
        <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-amber-200 bg-amber-500/10 border border-amber-300/30 rounded-full px-3 py-1 relative">
          <HeartHandshake size={11}/> Public Service Announcement
        </div>
        <h1
          data-testid="psa-heading"
          className="mt-4 text-2xl sm:text-3xl md:text-4xl font-black tracking-tight leading-tight relative"
        >
          {psa.heading}
        </h1>
        {psa.isPlaceholder && (
          <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] text-amber-100/90 bg-black/20 border border-white/10 rounded-full px-2 py-0.5 relative">
            Placeholder copy — final PSA text lands soon
          </div>
        )}
      </div>

      {/* Narration control bar */}
      <div className="px-6 md:px-10 py-5 bg-neutral-50 border-b border-neutral-200 flex items-center gap-4 flex-wrap">
        <button
          type="button"
          onClick={playing ? pause : play}
          data-testid="psa-play-pause"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-green-600 hover:bg-green-700 text-white font-semibold shadow-sm transition-colors"
          aria-label={playing ? 'Pause narration' : 'Play narration'}
        >
          {playing ? <Pause size={16}/> : <Play size={16}/>}
          {playing ? 'Pause' : (needsTap ? 'Tap to Play' : 'Play')}
        </button>
        <button
          type="button"
          onClick={replay}
          data-testid="psa-replay"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white hover:bg-neutral-100 text-neutral-800 font-semibold border border-neutral-300 transition-colors"
          aria-label="Replay from the start"
        >
          <RotateCcw size={16}/> Replay
        </button>
        <div className="flex-1 min-w-[140px] flex items-center gap-2">
          <Volume2 size={14} className="text-neutral-500"/>
          <div className="relative h-1.5 flex-1 bg-neutral-200 rounded-full overflow-hidden" data-testid="psa-progress">
            <div
              className="absolute inset-y-0 left-0 bg-green-600 transition-[width] duration-150"
              style={{ width: `${Math.min(100, progress * 100)}%` }}
            />
          </div>
          <div className="text-[11px] text-neutral-500 tabular-nums w-16 text-right">
            {fmt(progress * duration)} / {fmt(duration)}
          </div>
        </div>
      </div>

      {/* Cinematic karaoke body — beats highlight + auto-scroll with the audio */}
      <div className="relative bg-white">
        {/* Soft top/bottom fades so beats fade in/out of the read window */}
        <div className="pointer-events-none absolute inset-x-0 top-0 h-14 bg-gradient-to-b from-white to-transparent z-10"/>
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-white to-transparent z-10"/>

        <div
          ref={scrollWrapRef}
          data-testid="psa-body"
          className="px-6 md:px-10 py-10 md:py-14 max-h-[62vh] md:max-h-[58vh] overflow-y-auto scroll-smooth"
          style={{ scrollbarGutter: 'stable' }}
        >
          {beats.length === 0 ? (
            <p className="text-neutral-500 italic">No PSA copy yet.</p>
          ) : (
            <div className="space-y-4 max-w-2xl mx-auto">
              {beats.map((b, i) => {
                const isActive = i === activeIndex;
                const isPast   = i < activeIndex;
                return (
                  <p
                    key={b.index}
                    ref={(el) => { beatRefs.current[i] = el; }}
                    data-testid={`psa-beat-${i}`}
                    data-active={isActive || undefined}
                    className={[
                      'transition-all duration-500 ease-out will-change-transform',
                      'text-[17px] md:text-[19px] leading-[1.75] tracking-[-0.005em]',
                      isActive
                        ? 'text-emerald-900 font-semibold scale-[1.015] opacity-100 psa-beat-active'
                        : isPast
                          ? 'text-neutral-400 opacity-70'
                          : 'text-neutral-500 opacity-60',
                    ].join(' ')}
                  >
                    {b.text}
                  </p>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Hidden audio element — controls come from our buttons */}
      <audio
        ref={audioRef}
        src={audioUrl}
        preload="auto"
        onLoadedMetadata={(e) => { setDuration(e.currentTarget.duration || 0); setReady(true); }}
        onTimeUpdate={(e) => {
          const el = e.currentTarget;
          setProgress(el.duration ? el.currentTime / el.duration : 0);
        }}
        onEnded={() => { setPlaying(false); setProgress(1); }}
        onPause={() => setPlaying(false)}
        onPlay={() => setPlaying(true)}
        data-testid="psa-audio"
      />
      {!ready && (
        <div className="sr-only" aria-live="polite">Preparing narration…</div>
      )}
    </div>
  );
}
